import Tesseract from "tesseract.js";
import { BusinessCardType } from "../../../../utils/util-types";
export async function readText(image: string) {
  const worker = await Tesseract.createWorker("eng");

  await worker.setParameters({
    preserve_interword_spaces: "1",
    tessedit_pageseg_mode: Tesseract.PSM.AUTO,
    user_defined_dpi: "300",
  });

  const {
    data: { text },
  } = await worker.recognize(image);

  await worker.terminate();
  return text;
}
function normalizeText(text: string) {
  return text
    .replace(/[|]/g, "I")
    .replace(/\s{2,}/g, " ")
    .replace(/[,]/g, ",")
    .trim();
}
function getLines(text: string) {
  return normalizeText(text)
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 2);
}
function cleanLines(lines: string[]) {
  return lines
    .map((l) => l.trim())
    .filter(
      (l) =>
        l.length > 2 &&
        !/@/.test(l) &&
        !/\d{3,}/.test(l) &&
        !/(www|http|\.com)/i.test(l) &&
        !/(street|road|city|state|india|canada|ontario)/i.test(l)
    );
}

function scoreBusinessName(line: string, index: number, total: number) {
  let score = 0;

  // Prefer short names
  const wordCount = line.split(" ").length;
  if (wordCount >= 1 && wordCount <= 4) score += 3;

  // ALL CAPS or Title Case
  if (line === line.toUpperCase()) score += 4;
  if (/^[A-Z][a-z]+/.test(line)) score += 2;

  // Avoid personal names
  if (!/ceo|manager|designer|founder/i.test(line)) score += 2;

  // Position bias (top or bottom)
  if (index < total * 0.3 || index > total * 0.7) score += 2;

  return score;
}

function normalizeOCR(text: string) {
  return text
    .replace(/ at /gi, "@")
    .replace(/\s@\s/g, "@")
    .replace(/\s\.\s/g, ".")
    .replace(/\n/g, " ");
}

function extractEmail(text: string) {
  const normalized = normalizeOCR(text);

  const cleanText = normalized.replace(/\n/g, " ");

  let match = cleanText.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);

  if (match) return match[0];

  match = cleanText.match(
    /[a-zA-Z0-9._%+-]+(?:\s*@\s*|\s+)[a-zA-Z0-9.-]+(?:\s*\.\s*)[a-zA-Z]{2,}/
  );

  if (match) {
    return match[0].replace(/\s+/g, "");
  }

  // 3. fallback from website
  const domain = extractWebsite(text)?.replace(/^www\./, "");
  const name = extractName(getLines(text))?.split(" ")[0]?.toLowerCase();

  if (domain && name) {
    return `${name}@${domain}`;
  }

  return "";
}

function extractPhone(text: string) {
  return text.match(/(\+?\d[\d\s\-()]{8,}\d)/)?.[0]?.trim() || "";
}

function extractWebsite(text: string) {
  return text.match(/(https?:\/\/)?(www\.)?[a-z0-9-]+\.[a-z]{2,}/i)?.[0] || "";
}
const ADDRESS_KEYWORDS =
  /street|st\.?|road|rd\.?|ave|avenue|lane|ln\.?|block|sector|phase|area|city|state|country|india|canada|ontario|hamilton|zip|pin|postal/i;

function extractAddress(lines: string[]) {
  const addressLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // skip obvious non-address lines
    if (
      /@/.test(line) ||
      /(www|http)/i.test(line) ||
      /\+?\d[\d\s\-()]{8,}/.test(line)
    ) {
      continue;
    }

    // keyword-based detection
    if (ADDRESS_KEYWORDS.test(line) || /\d{2,}\s+[A-Za-z]/.test(line)) {
      addressLines.push(line);

      // capture next line if it looks like continuation
      if (
        lines[i + 1] &&
        !/@/.test(lines[i + 1]) &&
        !/\+?\d/.test(lines[i + 1]) &&
        lines[i + 1].length > 4
      ) {
        addressLines.push(lines[i + 1]);
      }
    }
  }

  // fallback: lines near email / phone icons
  if (addressLines.length === 0) {
    const index = lines.findIndex((l) => /(email|phone|www)/i.test(l));

    if (index !== -1) {
      const possible = lines.slice(index + 1, index + 4);
      addressLines.push(...possible);
    }
  }

  return addressLines.filter((v, i, a) => a.indexOf(v) === i).join(", ");
}

function extractName(lines: string[]) {
  const blacklist =
    /email|phone|www|http|street|road|canada|india|usa|designer|manager|ceo|founder|product/i;

  const candidates = lines.filter(
    (l) => !/@/.test(l) && !/\d/.test(l) && !blacklist.test(l) && l.length <= 35
  );

  // Case 1: ALL CAPS split (ADI + BARBU)
  if (
    candidates.length >= 2 &&
    /^[A-Z]{2,}$/.test(candidates[0]) &&
    /^[A-Z]{2,}$/.test(candidates[1])
  ) {
    return `${candidates[0]} ${candidates[1]}`;
  }

  // Case 2: Normal full name
  const normalName = candidates.find((l) =>
    /^[A-Z][A-Za-z]+\s[A-Z][A-Za-z]+/.test(l)
  );

  return normalName || candidates[0] || "";
}
const DESIGNATION_KEYWORDS = [
  "ceo",
  "cto",
  "cfo",
  "coo",
  "founder",
  "co-founder",
  "product manager",
  "project manager",
  "manager",
  "director",
  "designer",
  "developer",
  "engineer",
  "consultant",
  "marketing",
  "sales",
  "product",
  "business",
  "lead",
  "head",
  "owner",
];

// function extractDesignation(lines: string[], personName: string) {
//   let best = "";
//   let bestScore = 0;

//   const nameIndex = lines.findIndex(l =>
//     personName && l.toLowerCase().includes(personName.toLowerCase())
//   );

//   lines.forEach((line, index) => {
//     const clean = line.replace(/\s+/g, " ").trim();
//     const lower = clean.toLowerCase();

//     // Exclude junk
//     if (
//       /@/.test(clean) ||
//       /\d/.test(clean) ||
//       /(www|http|street|road|city|state|india)/i.test(clean)
//     ) return;

//     const hasKeyword = DESIGNATION_KEYWORDS.some(k =>
//       lower.includes(k)
//     );
//     if (!hasKeyword) return;

//     let score = 0;

//     if (clean.split(" ").length >= 2) score += 4;

//     if (clean.split(" ").length <= 4) score += 2;

//     if (clean === clean.toUpperCase()) score += 2;

//     if (nameIndex !== -1 && Math.abs(index - nameIndex) <= 2) {
//       score += 4;
//     }

//     if (score > bestScore) {
//       bestScore = score;
//       best = clean;
//     }
//   });

//   return best;
// }
function extractDesignation(lines: string[], personName: string) {
  let best = "";
  let bestScore = 0;

  const cleanLines = lines.map((l) => l.replace(/\s+/g, " ").trim());

  const nameIndex = cleanLines.findIndex(
    (l) => personName && l.toLowerCase().includes(personName.toLowerCase())
  );

  cleanLines.forEach((line, index) => {
    const lower = line.toLowerCase();

    // ❌ Exclusions
    if (
      /@/.test(line) ||
      /\d{3,}/.test(line) ||
      /(www|http|street|road|city|state|india|canada)/i.test(line)
    )
      return;

    let score = 0;

    // ✅ Position-based rule
    if (nameIndex !== -1 && index === nameIndex + 1) {
      score += 10;
    }

    // ✅ Array-based fuzzy keyword check
    const hasKeyword = DESIGNATION_KEYWORDS.some((k) => lower.includes(k));

    if (hasKeyword) {
      score += 5;
    }

    // ✅ Length rule
    const words = line.split(" ");
    if (words.length >= 1 && words.length <= 4) score += 3;

    // ✅ Capitalization
    if (/^[A-Z]/.test(line)) score += 2;

    if (score > bestScore) {
      bestScore = score;
      best = line;
    }
  });

  return best;
}
const ORG_KEYWORDS = [
  "company",
  "enterprises",
  "solutions",
  "studio",
  "technologies",
  "tech",
  "graphics",
  "architecture",
  "design",
  "consulting",
  "group",
  "services",
  "family",
  "soft",
];

function hasOrganizationKeyword(text: string): boolean {
  const lower = text.toLowerCase();

  return ORG_KEYWORDS.some((k) => new RegExp(`\\b${k}\\b`, "i").test(lower));
}

function extractOrganizationName(lines: string[], result: BusinessCardType) {
  let best = "";
  let bestScore = 0;

  lines.forEach((line) => {
    //const clean = line.replace(/\s+/g, " ").trim();
    //const lower = clean.toLowerCase();

    if (hasOrganizationKeyword(line)) {
      best = line;
    }
    // if (
    //   lower === result?.name?.toLowerCase() ||
    //   lower === result?.designation?.toLowerCase() ||
    //   lower === result?.address?.toLowerCase() ||
    //   /@/.test(clean) ||
    //   /\d{3,}/.test(clean) ||
    //   /(www|http)/i.test(clean)
    // )
    //   return;

    // let score = 0;

    // if (ORG_KEYWORDS.some((k) => new RegExp(`\\b${k}\\b`, "i").test(lower))) {
    //   best = line;
    //   score += 6;
    // }

    // if (clean.split(" ").length >= 2) score += 2;

    // if (clean === clean.toUpperCase()) score += 2;
    // if (/^[A-Z][a-z]/.test(clean)) score += 1;

    // if (/^[A-Z][a-z]+ [A-Z][a-z]+$/.test(clean)) {
    //   score -= 4;
    // }

    // if (score > bestScore) {
    //   bestScore = score;
    //   best = clean;
    // }
  });

  return best;
}

export function parseBusinessCard(text: string) {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 2);

  const result: BusinessCardType = {
    name: extractName(lines),
    email: extractEmail(text),
    phone: extractPhone(text),
    website: extractWebsite(text),
    address: extractAddress(lines),
    rawText: text,
    designation: "",
    organization: "",
  };
  result.designation = extractDesignation(lines, result.name);
  result.organization = extractOrganizationName(lines, result);

  return result;
}
